/**
 * The WebAuthn ceremony, by hand (M2.8, SECURITY.md §4.3, ADR-004).
 *
 * `@better-auth/passkey` speaks the SimpleWebAuthn JSON dialect: every binary field -
 * the challenge, the user handle, each credential id, the attestation - crosses the wire
 * as **base64url**, and `navigator.credentials` wants `ArrayBuffer`s. That translation
 * is the entire job of this file.
 *
 * ## Why not `PublicKeyCredential.parseCreationOptionsFromJSON`
 *
 * It does exactly this and is the right answer eventually, but it landed in Safari 17.4
 * and Chrome 119 and is still absent from enough installed browsers that a staff member
 * on a managed laptop would meet a blank "passkeys are not supported" where the ceremony
 * would in fact have worked. Sixty lines of base64url is cheaper than that support call.
 *
 * ## What the caller has to know
 *
 * - Everything here throws. `NotAllowedError` is the one to expect: it means the person
 *   dismissed the system sheet or let it time out, and it is **not** an error worth an
 *   alert - `describePasskeyError` turns it into a sentence that says so.
 * - `isPasskeySupported()` is a capability check, not a promise that a key exists. A
 *   browser can support WebAuthn and still have nothing to offer, which the ceremony
 *   reports as `NotAllowedError` like any other refusal.
 */

/* ------------------------------------------------------------------ *
 * base64url
 * ------------------------------------------------------------------ */

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes.buffer;
}

export function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/* ------------------------------------------------------------------ *
 * The JSON shapes, as the server sends and expects them
 * ------------------------------------------------------------------ */

interface CredentialDescriptorJson {
  id: string;
  type?: string;
  transports?: string[];
}

/** `GET /auth/passkey/generate-register-options`. */
export interface PasskeyRegistrationOptionsJson {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: string; alg: number }[];
  timeout?: number;
  excludeCredentials?: CredentialDescriptorJson[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: string;
  extensions?: Record<string, unknown>;
}

/** `GET /auth/passkey/generate-authenticate-options`. */
export interface PasskeyAuthenticationOptionsJson {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: CredentialDescriptorJson[];
  userVerification?: string;
  extensions?: Record<string, unknown>;
}

/** The body of `POST /auth/passkey/verify-registration`, under `response`. */
export interface PasskeyRegistrationResponseJson {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment?: string;
  clientExtensionResults: Record<string, unknown>;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: string[];
  };
}

/** The body of `POST /auth/passkey/verify-authentication`, under `response`. */
export interface PasskeyAuthenticationResponseJson {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment?: string;
  clientExtensionResults: Record<string, unknown>;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
}

/* ------------------------------------------------------------------ *
 * Capability
 * ------------------------------------------------------------------ */

/** Whether this browser can run a ceremony at all. Safe to call during render. */
export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.create === 'function' &&
    typeof navigator.credentials.get === 'function'
  );
}

/* ------------------------------------------------------------------ *
 * The ceremonies
 * ------------------------------------------------------------------ */

function descriptors(
  list: CredentialDescriptorJson[] | undefined,
): PublicKeyCredentialDescriptor[] {
  return (list ?? []).map((entry) => ({
    id: base64UrlToBuffer(entry.id),
    type: 'public-key' as const,
    ...(entry.transports === undefined
      ? {}
      : { transports: entry.transports as AuthenticatorTransport[] }),
  }));
}

/** Creates a passkey and returns the attestation in the shape the API verifies. */
export async function createPasskey(
  options: PasskeyRegistrationOptionsJson,
): Promise<PasskeyRegistrationResponseJson> {
  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: base64UrlToBuffer(options.challenge),
    rp: options.rp,
    user: {
      id: base64UrlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams.map((parameter) => ({
      type: 'public-key' as const,
      alg: parameter.alg,
    })),
    ...(options.timeout === undefined ? {} : { timeout: options.timeout }),
    excludeCredentials: descriptors(options.excludeCredentials),
    ...(options.authenticatorSelection === undefined
      ? {}
      : { authenticatorSelection: options.authenticatorSelection }),
    ...(options.attestation === undefined
      ? {}
      : { attestation: options.attestation as AttestationConveyancePreference }),
  };

  const credential = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null;
  if (credential === null) throw new Error('No passkey was created.');

  const response = credential.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === 'function' ? response.getTransports() : undefined;

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    ...(credential.authenticatorAttachment === null ||
    credential.authenticatorAttachment === undefined
      ? {}
      : { authenticatorAttachment: credential.authenticatorAttachment }),
    clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      ...(transports === undefined ? {} : { transports }),
    },
  };
}

/** Runs an assertion and returns it in the shape the API verifies. */
export async function assertPasskey(
  options: PasskeyAuthenticationOptionsJson,
): Promise<PasskeyAuthenticationResponseJson> {
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: base64UrlToBuffer(options.challenge),
    ...(options.timeout === undefined ? {} : { timeout: options.timeout }),
    ...(options.rpId === undefined ? {} : { rpId: options.rpId }),
    allowCredentials: descriptors(options.allowCredentials),
    ...(options.userVerification === undefined
      ? {}
      : { userVerification: options.userVerification as UserVerificationRequirement }),
  };

  const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (credential === null) throw new Error('No passkey was used.');

  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    ...(credential.authenticatorAttachment === null ||
    credential.authenticatorAttachment === undefined
      ? {}
      : { authenticatorAttachment: credential.authenticatorAttachment }),
    clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle === null ? null : bufferToBase64Url(response.userHandle),
    },
  };
}

/**
 * The sentence to show for a failed ceremony.
 *
 * The browser's own `DOMException` messages are written for developers ("The operation
 * either timed out or was not allowed…"), so the two that people actually meet get
 * plain replacements. Everything else falls through to the caller's error handling.
 */
export function describePasskeyError(error: unknown): string | null {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    // Dismissed the sheet, or no key matched. Not a failure worth alarming about.
    if (error.name === 'NotAllowedError') return 'Passkey sign-in was cancelled.';
    if (error.name === 'InvalidStateError')
      return 'This device already has a passkey for this account.';
    if (error.name === 'SecurityError') {
      return 'Passkeys need a secure connection to this exact address.';
    }
  }
  return null;
}
